import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge,
  STATUS_TONE, fmtDate, fmtMoney,
} from "../../components/app/Kit";

const LEAD_FILTERS = ["all", "new", "hot", "warm", "cold", "booked", "lost"];

export default function Leads() {
  const { data, state, error, reload } = useResource("/leads");
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", source: "Website Form" });
  const q = params.get("q") || "";

  const rows = useMemo(() => {
    let list = data || [];
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((l) =>
        [l.name, l.email, l.phone, l.company].filter(Boolean).some((v) => v.toLowerCase().includes(needle))
      );
    }
    if (filter !== "all")
      list = list.filter((l) => l.status === filter || l.temperature === filter);
    return list;
  }, [data, q, filter]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("A name and valid email are required.");
      return;
    }
    try {
      await api.post("/leads", {
        ...form, status: "new", temperature: "warm", score: 50, value: 0,
        assigned_to: "Unassigned", next_action: "First contact",
      });
      toast.success("Lead created.");
      setCreating(false);
      setForm({ name: "", email: "", phone: "", company: "", source: "Website Form" });
      reload();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      toast.success(`${lead.name} moved to ${status}.`);
      reload();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="leads-page">
      <PageTitle
        testId="leads-title"
        title="Leads"
        sub="Every enquiry with an owner, a score and a next action."
        action={
          <div className="flex items-center gap-2">
            <DemoBadge />
            <button
              type="button"
              onClick={() => setCreating((v) => !v)}
              data-testid="leads-new-button"
              className="inline-flex items-center gap-2 rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]"
            >
              <Icons.Plus className="h-3.5 w-3.5" /> New lead
            </button>
          </div>
        }
      />

      {creating ? (
        <Panel testId="leads-create-panel" title="Create lead" className="mb-4">
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-5">
            {[["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"], ["source", "Source"]].map(
              ([key, label]) => (
                <div key={key}>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">{label}</label>
                  <input
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    data-testid={`lead-input-${key}`}
                    className="mt-2 w-full rounded-md border border-elv-line bg-white/[0.02] px-3 py-2 text-xs text-elv-text outline-none focus:border-elv-green/40"
                  />
                </div>
              )
            )}
            <div className="sm:col-span-5">
              <button
                type="submit"
                data-testid="lead-create-submit"
                className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]"
              >
                Save lead
              </button>
            </div>
          </form>
        </Panel>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 sm:max-w-xs">
          <Icons.Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-elv-dim" />
          <input
            value={q}
            onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {})}
            placeholder="Search name, phone, email, company"
            data-testid="leads-search"
            className="w-full rounded-md border border-elv-line bg-white/[0.02] py-2 pl-9 pr-3 text-xs text-elv-text outline-none focus:border-elv-green/40"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {LEAD_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              data-testid={`leads-filter-${f}`}
              className={`rounded-md border px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] transition-colors duration-200 ${
                filter === f
                  ? "border-elv-green/35 bg-elv-green/[0.08] text-elv-green"
                  : "border-elv-line bg-white/[0.02] text-elv-dim hover:text-elv-text"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {state === "loading" ? <Loading rows={8} testId="leads-loading" /> : null}
      {state === "error" ? <ErrorState message={error} onRetry={reload} testId="leads-error" /> : null}

      {state === "ready" && rows.length === 0 ? (
        <EmptyState
          icon="Target"
          title="No leads yet"
          sub="Your first lead will appear here as soon as a form, call or message comes in."
          action={
            <button
              type="button"
              onClick={() => setCreating(true)}
              data-testid="leads-empty-cta"
              className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]"
            >
              Add a lead manually
            </button>
          }
          testId="leads-empty"
        />
      ) : null}

      {state === "ready" && rows.length > 0 ? (
        <>
          {/* Desktop table */}
          <div className="glass hidden overflow-hidden rounded-lg lg:block">
            <table data-testid="leads-table" className="w-full text-left text-xs">
              <thead className="border-b border-elv-line text-[10px] uppercase tracking-[0.16em] text-elv-dim">
                <tr>
                  {["Lead", "Source", "Status", "Temp", "Score", "Owner", "Next action", "Last contact", "Value", ""].map((h) => (
                    <th key={h} className="px-4 py-3 font-normal">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <Link to={`/app/customers/${l.customer_id}`} className="font-medium text-elv-text hover:text-elv-green">
                        {l.name}
                      </Link>
                      <p className="mt-1 text-[11px] text-elv-dim">{l.phone}</p>
                    </td>
                    <td className="px-4 py-3 text-elv-dim">{l.source}</td>
                    <td className="px-4 py-3">
                      <Pill tone={STATUS_TONE[l.status]}>{l.status}</Pill>
                    </td>
                    <td className="px-4 py-3">
                      <Pill tone={STATUS_TONE[l.temperature]}>{l.temperature}</Pill>
                    </td>
                    <td className="px-4 py-3 font-mono text-elv-text">{l.score}</td>
                    <td className="px-4 py-3 text-elv-dim">{l.assigned_to}</td>
                    <td className="px-4 py-3 text-elv-dim">{l.next_action}</td>
                    <td className="px-4 py-3 text-elv-dim">{fmtDate(l.last_contact)}</td>
                    <td className="px-4 py-3 text-elv-text">{fmtMoney(l.value)}</td>
                    <td className="px-4 py-3">
                      <select
                        value={l.status}
                        onChange={(e) => setStatus(l, e.target.value)}
                        data-testid={`lead-status-select-${l.id}`}
                        className="rounded-md border border-elv-line bg-[#0A0F14] px-2 py-1 text-[11px] text-elv-text outline-none"
                      >
                        {["new", "contacted", "qualified", "booked", "won", "lost"].map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div data-testid="leads-cards" className="grid gap-2 lg:hidden">
            {rows.map((l) => (
              <div key={l.id} data-testid={`lead-card-${l.id}`} className="glass rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/app/customers/${l.customer_id}`} className="text-sm font-medium text-elv-text">
                    {l.name}
                  </Link>
                  <Pill tone={STATUS_TONE[l.status]}>{l.status}</Pill>
                </div>
                <p className="mt-2 text-[11px] text-elv-dim">
                  {l.phone} · {l.source}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Pill tone={STATUS_TONE[l.temperature]}>{l.temperature}</Pill>
                  <span className="font-mono text-[11px] text-elv-dim">score {l.score}</span>
                  <span className="text-[11px] text-elv-text">{fmtMoney(l.value)}</span>
                </div>
                <p className="mt-3 text-[11px] text-elv-dim">Next: {l.next_action}</p>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
